/**
 * outboxFailures — FT-577 (2026-11-01). Failed-upload visibility + recovery for
 * the FT-234 outbox. Split out of outbox.js (400-line convention); every
 * read-modify-write still goes through outbox's FT-445 `withOutboxLock`.
 *
 *  - getOutboxFailureSummary  → what the header sync badge shows
 *  - retryAllFailedItems      → the badge's "Try again" (safe photo uploads ONLY)
 *  - requeueFailedEntry       → the photo reconcile safety net (photoPullMerge)
 */

import { writeJSON } from './storageRouter';
import {
  listOutbox,
  withOutboxLock,
  notifyOutboxStatus,
  OUTBOX_KEY,
  MAX_OUTBOX_RETRIES,
  FAILURE_KIND_BLOCKED_EMPTY,
  CHANGE_TYPES,
} from './outbox';
import { db } from './db';
import { buildPhotoCloudRecord } from '../components/common/PhotoAttachment/hooks/buildPhotoCloudRecord';
import { computePushBodyBytesForRecord, SERVER_MAX_PUSH_BODY_BYTES } from '../utils/photoPayloadBudget';

/**
 * True for a failed entry that represents a deliberate safety block (the
 * empty-push guard refused to wipe cloud data), not unsynced user data. Legacy
 * entries pre-date `failureKind`, so the guard's message is matched too.
 */
export const isBenignBlockedFailure = (item) =>
  !!item && (item.failureKind === FAILURE_KIND_BLOCKED_EMPTY
    || (typeof item.error === 'string' && item.error.startsWith('Empty push blocked')));

/**
 * Summary of genuinely-unsynced FAILED outbox items, for the header sync badge.
 * "permanent" = gave up automatic retries; "retrying" = failed but will be
 * retried automatically on next app start / reconnect.
 * @returns {Promise<{ permanentCount:number, permanentPhotoCount:number, retryingCount:number, retryingPhotoCount:number, items:Array }>}
 */
export const getOutboxFailureSummary = async () => {
  const outbox = await listOutbox();
  const failed = (Array.isArray(outbox) ? outbox : [])
    .filter(i => i && i.status === 'failed' && !isBenignBlockedFailure(i));
  const isPhoto = (i) => i.collection === 'sr.photos.v1';
  const isPermanent = (i) => (i.retryCount || 0) >= MAX_OUTBOX_RETRIES;
  const permanent = failed.filter(isPermanent);
  const retrying = failed.filter(i => !isPermanent(i));
  return {
    permanentCount: permanent.length,
    permanentPhotoCount: permanent.filter(isPhoto).length,
    retryingCount: retrying.length,
    retryingPhotoCount: retrying.filter(isPhoto).length,
    items: failed.map(i => ({
      id: i.id,
      collection: i.collection,
      recordId: i.recordId,
      error: i.error || null,
      lastStatus: typeof i.lastStatus === 'number' ? i.lastStatus : null,
      lastRetry: i.lastRetry || null,
      retryCount: i.retryCount || 0,
      permanent: isPermanent(i),
      isPhoto: isPhoto(i),
    })),
  };
};

const PHOTOS_COLLECTION = 'sr.photos.v1';

const isPhotoCreate = (e) => !!e && e.collection === PHOTOS_COLLECTION
  && e.type === CHANGE_TYPES.CREATE && !!e.recordId;

/** photoIds with a delete marker anywhere in the outbox (pending OR failed). */
const deletedPhotoIdsIn = (outbox) => new Set(outbox
  .filter(e => e && e.collection === PHOTOS_COLLECTION && e.type === CHANGE_TYPES.DELETE && e.recordId)
  .map(e => e.recordId));

/** photoIds that already have another LIVE (not failed) upload queued. */
const liveCreatePhotoIdsIn = (outbox) => new Set(outbox
  .filter(e => isPhotoCreate(e) && e.status !== 'failed')
  .map(e => e.recordId));

/**
 * The photo as it is on THIS device right now, or null when it must not be
 * re-sent (deleted / gone, belongs to another vessel, or no thumbnail).
 */
const loadLocalPhoto = async (photoId, vesselId) => {
  const photo = await db.photos.get(photoId);
  if (!photo?.blob || (vesselId && photo.vesselId !== vesselId)) return null;
  const thumbRow = await db.photoThumbs.get(photoId);
  if (!thumbRow?.blob) return null;
  return { photo, thumbBlob: thumbRow.blob };
};

/**
 * Fresh upload for a failed photo CREATE, rebuilt from the photo as it is on
 * this device now (same builder + size check as the reconcile safety net), or
 * null when it must not be re-sent.
 */
const rebuildSafePhotoUpload = async (item, { deletedIds, liveIds }) => {
  if (deletedIds.has(item.recordId) || liveIds.has(item.recordId)) return null;
  try {
    const local = await loadLocalPhoto(item.recordId, item.vesselId);
    if (!local) return null;
    const record = await buildPhotoCloudRecord({
      photoData: local.photo, thumbBlob: local.thumbBlob, vesselId: item.vesselId,
    });
    if (computePushBodyBytesForRecord(record, item.vesselId) > SERVER_MAX_PUSH_BODY_BYTES) return null;
    return record;
  } catch (_err) {
    return null;
  }
};

/**
 * Phase 2 (under the FT-445 lock): re-check each rebuilt entry against the
 * CURRENT outbox and device — the user may have deleted the photo meanwhile —
 * then requeue it in place (same entry id; fresh retry budget; a manual retry
 * also restores the safety net's revival budget, as before).
 * @returns {Promise<number>} how many were requeued
 */
const requeueRebuiltPhotoUploads = (rebuilt) => withOutboxLock(async () => {
  const outbox = await listOutbox();
  const deletedNow = deletedPhotoIdsIn(outbox);
  const liveNow = liveCreatePhotoIdsIn(outbox);
  let n = 0;
  for (const item of outbox) {
    if (!rebuilt.has(item.id) || item.status !== 'failed') continue;
    if (deletedNow.has(item.recordId) || liveNow.has(item.recordId)) continue;
    // eslint-disable-next-line no-await-in-loop
    if (!(await db.photos.get(item.recordId))) continue;
    item.data = rebuilt.get(item.id);
    item.status = 'pending';
    item.error = null;
    item.retryCount = 0;
    item.reconcileAttempts = 0;
    n += 1;
  }
  if (n > 0) await writeJSON(OUTBOX_KEY, outbox);
  return n;
});

/**
 * User-initiated "Try again" (FT-577 review fix — SAFETY-CRITICAL).
 *
 * ONLY photo uploads (`sr.photos.v1` CREATE) are re-sent, and only when the
 * photo still exists on this device and has not been deleted. Each one is
 * REBUILT from the photo's current on-device state — the same builder, size
 * check and in-place requeue the boot-time safety net uses
 * (photoPullMerge.reconcileVesselPhotos) — never the stale queued payload.
 *
 * Everything else is deliberately NOT touched:
 *  - Other (non-photo) changes: for single-document collections the server's
 *    /sync/push is a blind upsert, so re-sending a long-dead change would
 *    silently overwrite newer data written since by another device (the
 *    FT-446 bug class). Permanently-failed ones stay failed and visible
 *    (badge tells the user to contact support); ones still inside their
 *    automatic retries are left to the automatic retry.
 *  - A photo with a queued delete marker, or no longer on this device: a
 *    deleted photo must stay deleted (its stale CREATE would overwrite the
 *    delete marker on the server). Its entry is left as-is (never removed — if
 *    the photo vanished for any other reason it may be the last copy).
 *  - A photo whose rebuilt upload is still over the server limit (would just
 *    fail again).
 *
 * @returns {Promise<{ requeued:number, notRetriedPhotos:number, notRetriedOther:number }>}
 *   notRetriedPhotos = failed photo uploads that could not be safely re-sent;
 *   notRetriedOther  = PERMANENTLY-failed non-photo changes (need support).
 */
export const retryAllFailedItems = async () => {
  const result = { requeued: 0, notRetriedPhotos: 0, notRetriedOther: 0 };
  try {
    const snapshot = await listOutbox();
    const failed = (Array.isArray(snapshot) ? snapshot : [])
      .filter(i => i && i.status === 'failed' && !isBenignBlockedFailure(i));
    const guards = { deletedIds: deletedPhotoIdsIn(snapshot), liveIds: liveCreatePhotoIdsIn(snapshot) };

    // Phase 1 (no lock — FileReader/IndexedDB reads are slow): rebuild each
    // safe photo upload from the device; count everything that is not re-sent.
    const rebuilt = new Map(); // entryId -> fresh cloud record
    for (const item of failed) {
      if (isPhotoCreate(item)) {
        // eslint-disable-next-line no-await-in-loop
        const record = await rebuildSafePhotoUpload(item, guards);
        if (record) rebuilt.set(item.id, record); else result.notRetriedPhotos += 1;
      } else if (item.collection === PHOTOS_COLLECTION) {
        result.notRetriedPhotos += 1; // photo DELETE etc. — not re-sent here
      } else if ((item.retryCount || 0) >= MAX_OUTBOX_RETRIES) {
        result.notRetriedOther += 1; // dead non-photo change — needs support
      } // else: non-photo still inside its automatic retries — left to them
    }

    if (rebuilt.size > 0) {
      result.requeued = await requeueRebuiltPhotoUploads(rebuilt);
      result.notRetriedPhotos += rebuilt.size - result.requeued;
    }
    if (result.requeued > 0) notifyOutboxStatus({ status: 'retry' });
    return result;
  } catch (_err) {
    return result;
  }

};

/**
 * Used by the photo reconcile safety net. Replaces a FAILED entry's payload IN
 * PLACE (same entry id — no duplicate notes) with a freshly built record and
 * gives it a fresh retry budget. `reconcileAttempts` counts how many times the
 * safety net has done this, so it can't loop forever.
 * @returns {Promise<boolean>} true if the entry was re-queued
 */
export const requeueFailedEntry = async (entryId, newData) => {
  try {
    const done = await withOutboxLock(async () => {
      const outbox = await listOutbox();
      const item = outbox.find(i => i.id === entryId);
      if (!item || item.status !== 'failed') return false;
      item.data = newData;
      item.status = 'pending';
      item.error = null;
      item.retryCount = 0;
      item.reconcileAttempts = (item.reconcileAttempts || 0) + 1;
      await writeJSON(OUTBOX_KEY, outbox);
      return true;
    });
    if (done) notifyOutboxStatus({ id: entryId, status: 'requeued' });
    return done;
  } catch (_err) {
    return false;
  }
};
